import FetchRequest from "@easylibs/fetch-request";
import Utils from "@easylibs/utils";
import ProgressForm from "./default-progress-form";
import {
  FetchFieldsetParams,
  FieldSetGetterData,
  LazyOptions,
} from "./scripts/interfaces";
import { cssStyle } from "./scripts/css-style";

/**
 * LazyProgressForm est une classe qui étend ProgressForm pour gérer des formulaires à étapes
 * avec une progression paresseuse (lazy loading) des fieldsets.
 */
export default class LazyProgressForm extends ProgressForm {
  private isLazyRunCalled = false;
  private _buttonInner: string;

  /**
   * Constructeur de la classe LazyProgressForm.
   * @param {HTMLFormElement} form - L'élément de formulaire HTML.
   * @param {string} url - L'URL à laquelle les données du formulaire sont envoyées.
   * @param {string} [parentTarget] - Sélecteur pour le conteneur parent des fieldsets.
   */
  public constructor(
    public form: HTMLFormElement,
    public url: string,
    public parentTarget?: string
  ) {
    super(form);
  }

  /**
   * Initialise la progression paresseuse des fieldsets.
   * @returns {LazyProgressForm} - Retourne l'instance de LazyProgressForm.
   */
  public lazyRun(lazyOptions: LazyOptions): LazyProgressForm {
    this.isLazyRunCalled = true;
    this.isLazyProgress = true;
    let { fieldsetLength, progressOptions, styleOptions } = lazyOptions;

    this.lazyFieldsetLength = fieldsetLength;
    let { fieldSets, nextIndex, prevTranslateX, PROGRESS, progressElement } =
      this.init(progressOptions);
    let translateX =
      this.translateX - this.fieldsetMarginWidth / this.fieldsetLength;
    const targetMarginWidth = progressOptions
      ? progressOptions.targetMarginWidth ?? 0
      : 0;
    for (let i = 0; i < this.lazyFieldsetLength; i++) {
      const fieldSet = (
        i === 0 ? document.querySelector(`fieldset`) : null
      ) as HTMLElement;
      const nextButton = fieldSet?.querySelector("[__next__]") as HTMLElement;
      let nextTranslateX = translateX * nextIndex - targetMarginWidth;
      prevTranslateX = translateX * nextIndex + Math.abs(translateX * 2);
      const nextProgress = PROGRESS * (i + 2);
      const prevProgress = i > 1 ? PROGRESS * i : PROGRESS;
      this.fieldSetElement = fieldSet;
      if (fieldSet) {
        fieldSet.classList.add(`fieldset0`);
        fieldSet.focus();
        this.compartmentalizeFocusInFieldset(fieldSet);
        nextButton.setAttribute("data-next-index", "1");
      }
      this.progressingData[`fieldset${i}`] = {
        "@i": i,
        "@translateX":
          i === 0 ? 0 : i === 1 ? translateX : nextTranslateX - translateX,
        "@progress": PROGRESS * (i + 1),
        "@target": this.fieldSetElement,
        next: {
          i: nextIndex,
          button: nextButton,
          translateX: nextTranslateX,
          progress: nextProgress,
        },
        prev: {
          i: i - 1,
          button: null,
          translateX: prevTranslateX,
          progress: prevProgress,
        },
      };
      nextIndex++;
      this.formatProgressingData(i);
    }
    if (progressElement) {
      progressElement.style.width = `${PROGRESS}%`;
    }
    if (this.enableDefaultCssStyle) {
      this.renderedStyle = cssStyle(
        this.form,
        fieldSets,
        this.translateX,
        this.fieldsetLength,
        this.fieldsetMarginWidth,
        styleOptions
      );
    }
    return this;
  }

  /**
   * Gère la récupération et l'affichage du prochain fieldset.
   * @param {FieldSetGetterData} data - Les données nécessaires pour récupérer le prochain fieldset.
   */
  public fetchNextFieldSet(data: FieldSetGetterData) {
    if (!this.isLazyRunCalled) {
      throw new Error("You must call LazyProgressForm.lazyRun() before.");
    }

    let {
      callbacks,
      spinner,
      template,
      shouldRepost,
      extraData,
      preventSubmit,
      submitAllData,
    } = data;
    template = template ?? this.form.querySelector("fieldset");
    const nextButton = template.querySelector("[__next__]") as HTMLElement;

    if (nextButton) {
      nextButton.addEventListener("click", (e) => {
        e.preventDefault();
        if (!this.isValidFieldset(template)) {
          return;
        }

        this._buttonInner = nextButton.innerHTML;
        const index = parseInt(nextButton.dataset.nextIndex!);
        let existingFieldset = this.form?.querySelector(
          `.fieldset${index}`
        ) as HTMLFieldSetElement;

        const fetchParams: FetchFieldsetParams = {
          index,
          spinner,
          extraData,
          nextButton,
          nextButtonInner: this._buttonInner,
          callbacks:{
            onPreFetch:(data) => callbacks?.onPreFetch ? callbacks.onPreFetch(data, index) : undefined,
            onPostFetch:(response, status)=>{
              this.handleSpinner(nextButton, spinner, "remove");

              const elements = this.graftEvents(response, index);
              this.prepareNextStep(elements, {
                ...fetchParams,
                shouldRepost,
                submitAllData,
                preventSubmit,
              });
            },
            onError:(error, status) => callbacks?.onError ? callbacks?.onError(error, status, index) : undefined,
            onSuccess:(response, index, ...data)=> callbacks?.onSuccess ? callbacks?.onSuccess(response, index, ...data) : undefined,
          } ,
          shouldRepost,
          submitAllData,
          preventSubmit,
        };

        if (existingFieldset) {
          this.processExistingFieldset(fetchParams);
        } else {
          this.postFieldsetData(template, fetchParams);
        }
      });
    }
  }
  /**
   * Processes an existing fieldset.
   * @param {FetchFieldsetParams} params - Parameters for fetching the fieldset.
   */
  private processExistingFieldset(params: FetchFieldsetParams) {
    if (params.shouldRepost) {
      const previousFieldset = this.form?.querySelector(
        `.fieldset${params.index - 1}`
      ) as HTMLFieldSetElement;
      if (previousFieldset) {
        this.postFieldsetData(previousFieldset, params);
      }
    } else {
      this._next(params.index);
    }
  }
  /**
   * Posts fieldset data to the server.
   * @param {HTMLFieldSetElement} fieldset - The fieldset element to post.
   * @param {FetchFieldsetParams} params - Parameters for posting the fieldset data.
   */
  private postFieldsetData(
    fieldset: HTMLFieldSetElement,
    params: FetchFieldsetParams
  ) {
    const form =
      params.submitAllData && params.submitAllData === "atEachStep"
        ? this.form
        : undefined;
    new FetchRequest({
      uri: this.url,
      data: this.getFormData(fieldset, params.index, params.extraData, form),
      options: {
        method: "POST",
        responseDataType: "json",
      },
      callbacks: {
        onPreFetch: (data) => {
          this.handleSpinner(params.nextButton, params.spinner, "add");
          return params.callbacks?.onPreFetch ? params.callbacks?.onPreFetch(data, params.index) : undefined;
        },
        onPostFetch:(response, status)=> params.callbacks?.onPostFetch ? params.callbacks?.onPostFetch(response, status, params.index) : undefined,
        onError: (error, status) => params.callbacks?.onError ? params.callbacks?.onError(error, status, params.index) : undefined,
        onSuccess:(response)=> params.callbacks?.onSuccess ? params.callbacks?.onSuccess(response, params.index) : undefined,
      },
    });
  }
  /**
   * Prepares the next step in the form progression.
   * @param {any} elements - The elements involved in the next step.
   * @param {FetchFieldsetParams} params - Parameters for fetching the next fieldset.
   */
  private prepareNextStep(elements: any, params: FetchFieldsetParams) {
    if (!elements) return;
    const nextButton = elements.nextButton;
    if (!nextButton) {
      if (params.preventSubmit) {
        const { submitButton } = elements;
        submitButton?.addEventListener("click", (e) => {
          this.handleSpinner(submitButton, params.spinner, "add");
          const form =
            params.submitAllData && params.submitAllData === "atEachStep"
              ? this.form
              : undefined;
          const formData =
            form || (params.submitAllData && params.submitAllData === "atEnd")
              ? new FormData(this.form)
              : this.getFormData(
                  elements.fieldset,
                  undefined,
                  params.extraData
                );

          new FetchRequest({
            uri: this.url,
            data: formData,
            options: {
              method: "POST",
              responseDataType: "json",
            },
            callbacks: params.callbacks,
          });
        });
      }
    } else {
      nextButton.setAttribute(
        "data-next-index",
        String(params.index + 1)
      );
      this.fetchNextFieldSet({template: elements.fieldset, ...params});
    }
  }

  /**
 * Retrieves the form data for the given fieldset.
 * @param {HTMLFieldSetElement} template - The fieldset from which to extract the data.
 * @param {string} [i] - The index of the fieldset (optional).
 * @param {Record<string, any>} [extraData] - Additional data to add to the form (optional).
 * @returns {FormData} - The form data as a FormData object.
 */
  public getFormData(
    template: HTMLFieldSetElement,
    i?: number,
    extraData?: Record<string, any>,
    form?: HTMLFormElement
  ): FormData {
    let formData = form ? new FormData(form) : new FormData();
    let fields = template.querySelectorAll(
      "input, select, textarea"
    ) as NodeListOf<HTMLInputElement>;

    if (!form) {
      let radioGroups: { [name: string]: boolean } = {};
      let seenFields: { [name: string]: Set<string> } = {};

      fields.forEach((field) => {
        if (!seenFields[field.name]) {
          seenFields[field.name] = new Set();
        }

        if (field.type === "radio") {
          if (!radioGroups[field.name]) {
            radioGroups[field.name] = false;
          }

          if (field.checked) {
            formData.append(field.name, field.value);
            radioGroups[field.name] = true;
          }
        } else if (field.type === "checkbox" && field.checked) {
          formData.append(field.name, field.value);
        } else if (field.type === "file" && field.files && field.files.length > 0) {
          // Ajouter tous les fichiers sélectionnés au FormData
          Array.from(field.files).forEach((file) => {
            formData.append(field.name, file);
          });
        } else if (field.type !== "checkbox" && field.type !== "file") {
          // Vérifier si la valeur du champ est déjà dans le FormData
          if (!seenFields[field.name].has(field.value)) {
            formData.append(field.name, field.value);
            seenFields[field.name].add(field.value);
          }
        }
      });

      // S'assurer que tous les groupes radio ont une valeur, même si aucun n'est sélectionné
      fields.forEach((field) => {
        if (field.type === "radio" && !radioGroups[field.name]) {
          formData.append(field.name, "");
          radioGroups[field.name] = true;
        }
      });
    }

    if (i) {
      formData.set("nextIndex", i.toString());
    }
    if (extraData) {
      for (const [key, value] of Object.entries(extraData)) {
        formData.set(key, value);
      }
    }

    return formData;
  }


  /**
   * Insère le fieldset reçu dans le DOM et gère les événements.
   * @param {any} response - La réponse du serveur contenant le template du fieldset.
   * @param {number} i - L'indice du fieldset actuel.
   * @returns {Record<string,any> | null} - Contient les éléments du fieldset, les boutons prev/next et le bouton submit.
   */
  protected graftEvents(response: any, i: number): Record<string, any> | null {
    const fieldsetContainer = document.querySelector(
      `${this.parentTarget ? this.parentTarget + " " : ""}[fieldset__container]`
    ) as HTMLElement;
    const fieldset = Utils.textToHTMLElement(
      response.template
    ) as HTMLFieldSetElement;
    if (!fieldset) {
      return null;
    }
    const prevButton = fieldset.querySelector("[__prev__]") as HTMLElement;
    const nextButton = fieldset.querySelector("[__next__]") as HTMLElement;
    const submitButton = fieldset.querySelector("[__submit__]") as HTMLElement;
    const existingFieldset = document.querySelector(`.fieldset${i}`);
    if (!existingFieldset) fieldsetContainer.appendChild(fieldset);

    Object.assign(fieldset.style, this.RENDERED_STYLE.fieldsetStyle);
    this._prev(i, prevButton);
    if (this.fieldsetLength !== i) this._next(i);

    return { nextButton, prevButton, fieldset, submitButton };
  }

  /**
   * Déplace la progression vers le fieldset suivant.
   * @param {number} i - L'indice du fieldset actuel.
   */
  private _next(i: number) {
    const nextProgressingData = this.getProgressingData(i, "next");
    this.next(nextProgressingData.index, nextProgressingData.translate);
  }

  /**
   * Déplace la progression vers le fieldset précédent.
   * @param {number} i - L'indice du fieldset actuel.
   * @param {HTMLElement} [prevButton] - Le bouton "prev" (facultatif).
   */
  private _prev(i: number, prevButton?: HTMLElement) {
    const prevProgressingData = this.getProgressingData(i, "prev");
    this.prev(
      prevProgressingData.index,
      prevProgressingData.translate,
      prevButton
    );
  }

  /**
   * Récupère les données de progression pour le fieldset actuel.
   * @param {number} i - L'indice du fieldset actuel.
   * @param {string} type - Le type de progression ("next" ou "prev").
   * @returns {Record<string,any>} - Les données de progression pour le fieldset.
   */
  private getProgressingData(i: number, type: string): Record<string, any> {
    const PROGRESSING_DATA = this.PROGRESSING_DATA;
    const data = {
      next: {
        index: PROGRESSING_DATA[`fieldset${i}`]["@i"],
        translate: PROGRESSING_DATA[`fieldset${i}`]["@translateX"],
      },
      prev: {
        index: PROGRESSING_DATA[`fieldset${i}`].prev.i,
        translate: PROGRESSING_DATA[`fieldset${i}`].prev.translateX,
      },
    } as any;
    return data[`${type}`];
  }
  /**
   * Handles adding or removing a spinner element.
   * @param {HTMLElement} nextButton - The button triggering the spinner.
   * @param {HTMLElement} spinner - The spinner element.
   * @param {string} action - The action to perform ("add" or "remove").
   */
  private handleSpinner(
    button: HTMLElement,
    spinner: string | HTMLElement,
    action: "add" | "remove"
  ) {
    button.innerHTML = "";
    if (action === "add") {
      if (typeof spinner === "string") {
        button.innerHTML = spinner;
      } else {
        button.appendChild(spinner);
      }

      button.setAttribute("disabled", "disabled");
    }
    if (action === "remove") {
      button.innerHTML = this._buttonInner;
      button.removeAttribute("disabled");
    }
  }
}
